import React, { useEffect, useState } from 'react';
import { RegistrationData } from '../types';

const AdminRegistrations: React.FC = () => {
  const [registrations, setRegistrations] = useState<RegistrationData[]>([]);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('All');
  const [selectedReg, setSelectedReg] = useState<RegistrationData | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = () => {
    const data = JSON.parse(localStorage.getItem('fintrack_registrations') || '[]');
    setRegistrations(data);
  };

  const updateStatus = (id: string, newStatus: RegistrationData['status']) => {
    const updated = registrations.map(r => r.id === id ? { ...r, status: newStatus } : r);
    localStorage.setItem('fintrack_registrations', JSON.stringify(updated));
    setRegistrations(updated);
    if (selectedReg?.id === id) {
      setSelectedReg({ ...selectedReg, status: newStatus });
    }
  };

  const deleteRegistration = (id: string) => {
    if (window.confirm('Are you sure you want to remove this registration file?')) {
      const updated = registrations.filter(r => r.id !== id);
      localStorage.setItem('fintrack_registrations', JSON.stringify(updated));
      setRegistrations(updated);
      setSelectedReg(null);
    }
  };

  const filtered = registrations.filter(r => {
    const matchesSearch = r.companyName.toLowerCase().includes(search.toLowerCase()) || 
                          r.founderName.toLowerCase().includes(search.toLowerCase()) ||
                          r.id.toLowerCase().includes(search.toLowerCase());
    const matchesFilter = filter === 'All' || r.status === filter;
    return matchesSearch && matchesFilter;
  });

  return (
    <div className="animate-reveal p-8 max-w-7xl mx-auto min-h-screen">
      <header className="mb-10 flex justify-between items-start">
        <div>
          <h1 className="text-4xl font-display font-bold text-slate-900 dark:text-white">Active Registry</h1>
          <p className="text-slate-500 mt-2">Manage all ongoing company registrations and compliance filings.</p>
        </div>
        <button 
          onClick={loadData}
          className="p-3 bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-slate-100 dark:border-slate-800 text-primary dark:text-accent-400 hover:rotate-180 transition-transform duration-500"
          title="Refresh Registry"
        >
          <span className="material-symbols-rounded">refresh</span>
        </button>
      </header>

      <div className="flex flex-col md:flex-row gap-4 mb-8">
        <div className="relative flex-1">
          <span className="material-symbols-rounded absolute left-4 top-1/2 -translate-y-1/2 text-slate-400">search</span>
          <input 
            className="w-full pl-12 pr-4 py-4 bg-white dark:bg-slate-900 border-none rounded-2xl shadow-sm focus:ring-2 focus:ring-primary text-slate-900 dark:text-white"
            placeholder="Search by company, founder or Reference ID..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <div className="flex gap-2 p-1 bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-slate-100 dark:border-slate-800">
          {['All', 'Pending', 'Processing', 'Completed'].map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-6 py-3 rounded-xl text-xs font-black uppercase tracking-widest transition-all ${filter === f ? 'bg-primary text-white shadow-lg' : 'text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'}`}
            >
              {f}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-white dark:bg-slate-900 rounded-[32px] border border-slate-100 dark:border-slate-800 shadow-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-slate-50 dark:border-slate-800">
                <th className="px-8 py-6 text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Reference</th>
                <th className="px-8 py-6 text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Company Details</th>
                <th className="px-8 py-6 text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Service Type</th>
                <th className="px-8 py-6 text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Current Status</th>
                <th className="px-8 py-6 text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50 dark:divide-slate-800">
              {filtered.map((reg) => (
                <tr key={reg.id} className="group hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                  <td className="px-8 py-6">
                    <span className="font-mono text-xs font-bold text-primary dark:text-accent-400">{reg.id}</span>
                  </td>
                  <td className="px-8 py-6">
                    <div className="font-bold text-slate-900 dark:text-white">{reg.companyName}</div>
                    <div className="text-xs text-slate-400">{reg.founderName} • {reg.email}</div>
                  </td>
                  <td className="px-8 py-6">
                    <span className="text-sm font-semibold text-slate-600 dark:text-slate-300">{reg.service}</span>
                  </td>
                  <td className="px-8 py-6">
                    <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest ${
                      reg.status === 'Pending' ? 'bg-amber-100 text-amber-700' : 
                      reg.status === 'Processing' ? 'bg-blue-100 text-blue-700' :
                      'bg-green-100 text-green-700'
                    }`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${reg.status === 'Pending' ? 'bg-amber-500' : reg.status === 'Processing' ? 'bg-blue-500' : 'bg-green-500'}`}></span>
                      {reg.status}
                    </div>
                  </td>
                  <td className="px-8 py-6 text-right">
                    <div className="flex justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button 
                        onClick={() => setSelectedReg(reg)}
                        className="p-2 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 rounded-lg hover:bg-primary hover:text-white transition-all"
                        title="View Full Details"
                      >
                        <span className="material-symbols-rounded text-lg">visibility</span>
                      </button>
                      {reg.status !== 'Processing' && (
                        <button 
                          onClick={() => updateStatus(reg.id, 'Processing')}
                          className="p-2 bg-blue-50 dark:bg-blue-950/30 text-blue-600 rounded-lg hover:bg-blue-600 hover:text-white transition-all"
                          title="Set to Processing"
                        >
                          <span className="material-symbols-rounded text-lg">sync</span>
                        </button>
                      )}
                      {reg.status !== 'Completed' && (
                        <button 
                          onClick={() => updateStatus(reg.id, 'Completed')}
                          className="p-2 bg-green-50 dark:bg-green-950/30 text-green-600 rounded-lg hover:bg-green-600 hover:text-white transition-all"
                          title="Mark as Completed"
                        >
                          <span className="material-symbols-rounded text-lg">verified</span>
                        </button>
                      )}
                      <button 
                        onClick={() => deleteRegistration(reg.id)}
                        className="p-2 bg-red-50 dark:bg-red-950/30 text-red-600 rounded-lg hover:bg-red-600 hover:text-white transition-all"
                        title="Delete File"
                      >
                        <span className="material-symbols-rounded text-lg">delete</span>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={5} className="p-20 text-center">
                    <p className="text-slate-400 font-medium italic">No matches found in Registry.</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Detail Modal */}
      {selectedReg && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6">
          <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm animate-fade-in" onClick={() => setSelectedReg(null)}></div>
          <div className="relative bg-white dark:bg-slate-900 w-full max-w-2xl rounded-[40px] shadow-2xl overflow-hidden animate-reveal">
            <div className="p-8 border-b border-slate-100 dark:border-slate-800 flex justify-between items-center bg-slate-50 dark:bg-slate-900/50">
              <div>
                <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">File Reference</span>
                <h2 className="text-xl font-bold text-primary dark:text-accent-400 font-mono">{selectedReg.id}</h2>
              </div>
              <button onClick={() => setSelectedReg(null)} className="w-10 h-10 rounded-full bg-white dark:bg-slate-800 shadow-sm flex items-center justify-center text-slate-400 hover:text-red-500 transition-colors">
                <span className="material-symbols-rounded">close</span>
              </button>
            </div>
            
            <div className="p-8 space-y-8 max-h-[70vh] overflow-y-auto">
              <div className="grid grid-cols-2 gap-8">
                <div>
                  <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 block mb-1">Company Name</label>
                  <p className="font-bold text-slate-900 dark:text-white text-lg">{selectedReg.companyName}</p>
                </div>
                <div>
                  <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 block mb-1">Founder Name</label>
                  <p className="font-bold text-slate-900 dark:text-white text-lg">{selectedReg.founderName}</p>
                </div>
                <div>
                  <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 block mb-1">Requested Service</label>
                  <p className="font-bold text-primary dark:text-accent-400">{selectedReg.service}</p>
                </div>
                <div>
                  <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 block mb-1">Submission Date</label>
                  <p className="font-bold text-slate-700 dark:text-slate-300">{new Date(selectedReg.createdAt).toLocaleString()}</p>
                </div>
              </div>

              <div className="p-6 bg-slate-50 dark:bg-slate-800/50 rounded-3xl border border-slate-100 dark:border-slate-700">
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 block mb-4">Detailed Profile</label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                  <div className="flex justify-between border-b border-slate-200 dark:border-slate-700 pb-2">
                    <span className="text-slate-500">Industry:</span>
                    <span className="font-bold">{selectedReg.industry}</span>
                  </div>
                  <div className="flex justify-between border-b border-slate-200 dark:border-slate-700 pb-2">
                    <span className="text-slate-500">Employees:</span>
                    <span className="font-bold">{selectedReg.companySize}</span>
                  </div>
                  <div className="flex justify-between border-b border-slate-200 dark:border-slate-700 pb-2">
                    <span className="text-slate-500">Designation:</span>
                    <span className="font-bold">{selectedReg.designation}</span>
                  </div>
                  <div className="flex justify-between border-b border-slate-200 dark:border-slate-700 pb-2">
                    <span className="text-slate-500">Location:</span>
                    <span className="font-bold">{selectedReg.state}, {selectedReg.country}</span>
                  </div>
                  <div className="flex justify-between sm:col-span-2 pt-2">
                    <span className="text-slate-500">Contact:</span>
                    <span className="font-bold">{selectedReg.phone} • {selectedReg.email}</span>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 block">Workflow Management</label>
                <div className="flex flex-wrap gap-3">
                  <button 
                    onClick={() => updateStatus(selectedReg.id, 'Processing')}
                    className={`px-6 py-3 rounded-2xl font-bold text-xs flex items-center gap-2 transition-all ${selectedReg.status === 'Processing' ? 'bg-blue-600 text-white shadow-lg' : 'bg-blue-100 text-blue-700 hover:bg-blue-200'}`}
                  >
                    <span className="material-symbols-rounded text-lg">sync</span>
                    Move to Processing
                  </button>
                  <button 
                    onClick={() => updateStatus(selectedReg.id, 'Completed')}
                    className={`px-6 py-3 rounded-2xl font-bold text-xs flex items-center gap-2 transition-all ${selectedReg.status === 'Completed' ? 'bg-green-600 text-white shadow-lg' : 'bg-green-100 text-green-700 hover:bg-green-200'}`}
                  >
                    <span className="material-symbols-rounded text-lg">verified</span>
                    Mark as Completed
                  </button>
                  <button 
                    onClick={() => deleteRegistration(selectedReg.id)}
                    className="px-6 py-3 rounded-2xl bg-red-100 text-red-700 font-bold text-xs flex items-center gap-2 hover:bg-red-200"
                  >
                    <span className="material-symbols-rounded text-lg">delete</span>
                    Delete Registration
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminRegistrations;